import axios from 'axios';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { messages } from '@qality/common-messages';
import {
  Issue,
  TestCase,
  TestCasesCollection,
  TestStep,
  TestStepsCollection,
} from '@qality/common-model';
import { APResponse } from '@qality/common-model/src/AP/AP';
import { IssueTestType } from '@qality/common-model/src/TestType';
import {
  IssueLinkTypeService,
  StorageOfUniqueServices,
} from '@qality/common-service';
import {
  EntityUtils,
  ExpandUtils,
  LocalStorageUtils,
  StringUtils,
} from '@qality/common-utils';

import FlagService from './FlagService';
import IssueService from './IssueService';
import IssueTypeService from './IssueTypeService';
import TestStepService from './TestStepService';

const TEST_CASE_NAME_LIMIT = 255;
const TEST_CASE_NAME_SUFFIX = ' test case';

const DEFAULT_PAGE_SIZE = 1000;

type TestCaseWithTestTypeIds = { id: number; testTypeId: number };

export default {
  testCaseIssueTypeIds: {},
  async addNewTestCase(
    issueKey: string,
    testCases: TestCasesCollection,
    removeNotCreatedTestCase: (testCaseId: number) => void,
    updateCreatedTestCase: (testCaseId: number, testCaseName: string) => void,
    updateTestCaseIdsState?: (testCaseId: number) => void
  ): Promise<TestCasesCollection> {
    const id = EntityUtils.getNextId(testCases.testCases);

    const currentIssue = await IssueService.getIssue(issueKey);
    const sizeWithoutSuffix =
      TEST_CASE_NAME_LIMIT - TEST_CASE_NAME_SUFFIX.length;
    const name =
      StringUtils.truncate(currentIssue.fields.summary, sizeWithoutSuffix) +
      TEST_CASE_NAME_SUFFIX;
    const issue = this.createTestCase(
      id,
      issueKey,
      currentIssue,
      name,
      removeNotCreatedTestCase,
      updateCreatedTestCase,
      updateTestCaseIdsState
    );
    const testCase = new TestCase(
      id,
      name,
      new TestStepsCollection([new TestStep()]),
      issue,
      true
    );
    ExpandUtils.updateExpandValue(issueKey, testCase, true);
    testCases.testCases.push(testCase);
    return testCases;
  },

  async fetchTestCases(issue: Issue): Promise<TestCasesCollection> {
    const links = get(issue, 'fields.issuelinks', []);

    const [issues, testSteps] = await fetchTestCasesData(links);

    const testCases = links
      .map((link) => {
        const inwardOrOutward = link.inwardIssue
          ? link.inwardIssue
          : link.outwardIssue;

        if (isQAlityTestCase(inwardOrOutward)) {
          const properties = LocalStorageUtils.retrieveUserSettings(
            issue.key + '_testCasesProperties'
          );
          const expanded = get(
            properties,
            inwardOrOutward.id + '.expanded',
            false
          );
          return new TestCase(
            inwardOrOutward.id,
            inwardOrOutward.fields.summary,
            testSteps.get(inwardOrOutward.id),
            Promise.resolve(issues.get(inwardOrOutward.id)),
            expanded
          );
        }
      })
      .filter((testCase) => testCase);

    return new TestCasesCollection(testCases);
  },

  async paginateRequest<T>(
    items: Array<unknown>,
    chunkSize: number,
    fetchFunction: (chunk: Array<number>) => Promise<Array<T>>
  ): Promise<Array<T>> {
    const chunkArray = (array: unknown[], size: number) => {
      const result = [];
      for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
      }
      return result;
    };

    const chunks = chunkArray(items, chunkSize);
    const results: Array<T> = [];

    for (const chunk of chunks) {
      const chunkResults = await fetchFunction(chunk);
      results.push(...chunkResults);
    }

    return results;
  },

  async getTestCaseIdsMatchingFilters(
    testTypesIds: Array<number>
  ): Promise<Array<number>> {
    const finalParams = testTypesIds
      .map((testTypeId) => 'testTypesIds=' + testTypeId)
      .join('&');

    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_CASES +
        '/search',
      finalParams
    );

    const response = await axios.get(url);

    const data = response.data as Array<number>;

    return data;
  },

  async getIssueTestTypes(
    testCaseIds: Array<number>
  ): Promise<Array<IssueTestType>> {
    if (!StorageOfUniqueServices.get('LicenseService').isAdvanced) {
      return [];
    }
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_CASES
    );

    const fetchTestTypesForChunk = async (chunk: Array<number>) => {
      const requestBody = {
        testCaseIds: chunk,
        fields: ['testCaseId', 'testTypeId'],
      };

      const response = await this.retryRequest(() =>
        axios.post(url, requestBody, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
      );

      return response.data;
    };

    return this.paginateRequest(
      testCaseIds,
      DEFAULT_PAGE_SIZE,
      fetchTestTypesForChunk
    );
  },

  async retryRequest<T>(
    requestFn: () => Promise<T>,
    retries = 3,
    delay = 3000
  ): Promise<T> {
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        if (
          error.response &&
          (error.response.status === 403 || error.response.status === 404)
        ) {
          if (attempt < retries - 1) {
            await new Promise((resolve) => setTimeout(resolve, delay));
          } else {
            throw error;
          }
        }
      }
    }
  },

  deleteTestCaseLink(
    testCase: TestCase,
    issueKey: string
  ): Promise<APResponse> {
    const issuePromise = testCase.issue as Promise<Issue>;
    return issuePromise
      .then(async (testCaseIssue) => {
        const issue = await IssueService.getIssue(issueKey);
        const links = get(issue, 'fields.issuelinks', []);
        const testCaseLink = links.find((link) => {
          const key = link.inwardIssue?.key ?? link.outwardIssue?.key;
          return key === testCaseIssue.key;
        });
        return !isNil(testCaseLink)
          ? StorageOfUniqueServices.get('API').request({
              url: `/rest/api/2/issueLink/${testCaseLink.id}`,
              type: 'DELETE',
            })
          : null;
      })
      .catch((e) => {
        return e;
      });
  },

  updateTestCase(testCase: TestCase): void {
    const issuePromise = testCase.issue as Promise<Issue>;
    issuePromise
      .then((issue) => {
        if (get(issue, 'fields.summary') !== testCase.name) {
          IssueService.updateIssue(issue.key, testCase.name);
        }
        return true;
      })
      .catch(() => {
        return false;
      });
  },

  async bulkUpdateTestCaseTestTypes(
    testCaseWithTestTypeIds: Array<TestCaseWithTestTypeIds>
  ) {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_CASES +
        `/bulk`
    );

    const data = testCaseWithTestTypeIds.map((testCase) => {
      return { id: testCase.id, fields: { testTypeId: testCase.testTypeId } };
    });

    return await axios.patch(url, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },

  createTestCase(
    testCaseId: number,
    issueKey: string,
    issue: Issue,
    name: string,
    removeNotCreatedTestCase: (id: number) => void,
    updateCreatedTestCase: (id: number, testCaseName: string) => void,
    updateTestCaseIdsState?: (newTestCaseId: number) => void
  ): Promise<TestCase | { errorWhileCreating: true }> {
    // eslint-disable-next-line
    return new Promise<TestCase | { errorWhileCreating: true }>(
      // eslint-disable-next-line no-async-promise-executor
      async (resolve, reject) => {
        try {
          const issueTypeId = await this.getTestCaseIssueTypeId(
            issue.fields.project.id
          );
          const projectId = get(issue, 'fields.project.id');
          const response = await IssueService.createIssue(
            name,
            issueTypeId,
            projectId,
            false
          ).catch(async () => {
            const createdIssues = await StorageOfUniqueServices.get(
              'FlagCreator'
            ).showCreateIssueDialog(name, issueTypeId, projectId, issueKey);

            if (!createdIssues) {
              FlagService.createControlledFlag(
                messages['error.qalityIssueView.createTestCase.header'],
                messages['error.qalityIssueView.createTestCase.body'],
                'error',
                15000
              );
              removeNotCreatedTestCase(testCaseId);
              resolve({ errorWhileCreating: true });
            } else {
              if (createdIssues.length === 0) {
                FlagService.createFlag(
                  messages['warning.qalityIssueView.createTestCase.header'],
                  messages['warning.qalityIssueView.createTestCase.body'],
                  'warning'
                );
                removeNotCreatedTestCase(testCaseId);
                resolve({ errorWhileCreating: true });
              } else {
                const [firstCreatedIssue] = createdIssues;
                updateCreatedTestCase(
                  testCaseId,
                  firstCreatedIssue.fields.summary
                );
                return { body: JSON.stringify(firstCreatedIssue) };
              }
            }
          });
          const testCase = JSON.parse(response.body);
          testCase.fields = {
            summary: name,
          };
          const issueLinkTypeId =
            await IssueLinkTypeService.getQAlityRelationId();
          IssueService.issueLink(issueKey, testCase.key, issueLinkTypeId).catch(
            () => {
              StorageOfUniqueServices.get('FlagCreator').createLinkErrorFlag(
                testCase
              );
              removeNotCreatedTestCase(testCaseId);
              resolve({ errorWhileCreating: true });
            }
          );
          resolve(testCase);
          updateTestCaseIdsState?.(testCase.id);
        } catch (e) {
          reject(e);
        }
      }
    );
  },

  async getTestCaseIssueTypeId(projectId: string): Promise<number> {
    if (isEmpty(this.testCaseIssueTypeIds)) {
      const testCaseIssueTypes = await IssueTypeService.getTestCaseIssueTypes();
      testCaseIssueTypes.forEach((testCaseIssueType) => {
        const key = getQAlityTestIssueTypeKey(testCaseIssueType);
        if (key) {
          this.testCaseIssueTypeIds[key] = testCaseIssueType.id;
        }
      });
    }
    if (!isNil(this.testCaseIssueTypeIds[projectId])) {
      return this.testCaseIssueTypeIds[projectId];
    }
    return this.testCaseIssueTypeIds['global'];
  },
};

function getQAlityTestIssueTypeKey(testCaseIssueType) {
  const pId = get(testCaseIssueType, 'scope.project.id');
  const key = !isNil(pId) ? pId : 'global';

  return key;
}

async function fetchTestSteps(id) {
  const testSteps = await TestStepService.fetchTestSteps(id);
  return { id, testSteps };
}

async function fetchIssue(id) {
  const issue = await IssueService.getIssue(id);
  return { id, issue };
}

async function fetchTestCasesData(links) {
  const issuePromises = [];
  const testStepPromises = [];

  links.forEach((link) => {
    const inwardOrOutward = link.inwardIssue
      ? link.inwardIssue
      : link.outwardIssue;

    if (isQAlityTestCase(inwardOrOutward)) {
      testStepPromises.push(fetchTestSteps(inwardOrOutward.id));
      issuePromises.push(fetchIssue(inwardOrOutward.id));
    }
  });

  return [
    new Map(
      (await Promise.all(issuePromises)).map((data) => [data.id, data.issue])
    ),
    new Map(
      (await Promise.all(testStepPromises)).map((data) => [
        data.id,
        data.testSteps,
      ])
    ),
  ];
}

function isQAlityTestCase(inwardOrOutward: unknown): boolean {
  return get(inwardOrOutward, 'fields.issuetype.name') === 'QAlity Test';
}
